import * as React from "react";
import { Transition } from "react-transition-group";
import { useMediaQuery, useTheme } from "@mui/material";

const RISE = 12; // px — matches MotionInView's entrance rise, so dialogs share one motion language with scroll-in content
const DEFAULT_TIMEOUT = { enter: 240, exit: 180 };

// Minimal ref merge for the node ref + child's own ref + forwarded ref — not worth
// pulling in @mui/utils' unstable_useForkRef for a single use site.
//
// Must be memoised by the caller: React detaches a ref callback (calling it with
// null) whenever its identity changes, so a fresh function per render would
// repeatedly null out FocusTrap's internal root ref mid-transition.
function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") ref(node);
      else ref.current = node;
    });
  };
}

/**
 * Dialog open/close transition: fade + a subtle rise, mirroring the entrance
 * MotionInView uses for scroll-in content elsewhere in the app.
 *
 * Built directly on react-transition-group (what MUI's own Fade/Grow/Slide are
 * built on) rather than wrapping Fade, since Fade only ever transitions opacity —
 * there's no supported way to add a translateY to it.
 *
 * Falls back to a plain opacity fade (no movement) under prefers-reduced-motion.
 * Wired up as the default MuiDialog transition in the theme (index.js), so every
 * Dialog — StandardDialog, LibraryModal, and any ad-hoc ones — inherits it
 * automatically.
 */
const DialogTransition = React.forwardRef(function DialogTransition(props, ref) {
  const {
    children,
    in: inProp,
    appear = true,
    timeout = DEFAULT_TIMEOUT,
    easing,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    style,
    ...other
  } = props;

  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const nodeRef = React.useRef(null);
  const handleRef = React.useMemo(
    () => mergeRefs(nodeRef, children.ref, ref),
    [children.ref, ref],
  );

  const styles = prefersReducedMotion
    ? {
        entering: { opacity: 1 },
        entered: { opacity: 1 },
        exiting: { opacity: 0 },
        exited: { opacity: 0 },
      }
    : {
        entering: { opacity: 1, transform: "translateY(0)" },
        entered: { opacity: 1, transform: "translateY(0)" },
        exiting: { opacity: 0, transform: `translateY(${RISE}px)` },
        exited: { opacity: 0, transform: `translateY(${RISE}px)` },
      };

  const applyTransition = (node, duration) => {
    const properties = prefersReducedMotion ? "opacity" : ["opacity", "transform"];
    const transitionEasing = easing?.enter ?? theme.transitions.easing.easeOut;
    node.style.transition = theme.transitions.create(properties, {
      duration,
      easing: transitionEasing,
    });
  };

  // react-transition-group doesn't pass the DOM node to lifecycle callbacks when
  // `nodeRef` is used — it repurposes the first argument as `isAppearing` (or omits it
  // entirely for exit callbacks). Mirrors MUI Fade's normalizedTransitionCallback: always
  // read the node from nodeRef.current instead of trusting the callback argument.
  const normalized = (callback) => (maybeIsAppearing) => {
    if (!callback) return;
    if (maybeIsAppearing === undefined) callback(nodeRef.current);
    else callback(nodeRef.current, maybeIsAppearing);
  };

  const handleEnter = normalized((node, isAppearing) => {
    applyTransition(node, typeof timeout === "number" ? timeout : timeout.enter);
    onEnter?.(node, isAppearing);
  });

  const handleExit = normalized((node) => {
    applyTransition(node, typeof timeout === "number" ? timeout : timeout.exit);
    onExit?.(node);
  });

  return (
    <Transition
      nodeRef={nodeRef}
      appear={appear}
      in={inProp}
      timeout={timeout}
      onEnter={handleEnter}
      onEntering={normalized(onEntering)}
      onEntered={normalized(onEntered)}
      onExit={handleExit}
      onExiting={normalized(onExiting)}
      onExited={normalized(onExited)}
      {...other}
    >
      {(state, childProps) =>
        React.cloneElement(children, {
          ref: handleRef,
          style: {
            visibility: state === "exited" && !inProp ? "hidden" : undefined,
            ...styles[state],
            ...style,
            ...children.props.style,
          },
          // Everything Transition didn't consume must reach the child element, exactly
          // as MUI's own Fade/Grow/Slide do. Modal and Dialog pass the container's
          // `tabIndex="-1"` and `role="presentation"` down *through* the transition, so
          // dropping these silently breaks the dialog's focus trap.
          ...childProps,
        })
      }
    </Transition>
  );
});

export default DialogTransition;
